import type { CSSProperties } from 'react';

export const EDGE_COLOR = '#2563eb'; // blue

// How a selected run's outcome is coloured. The node cards draw their own
// outlines from the matching hexes in LBNodeDisplay.module.css; change both together.
export const RUN_COMPLETED_COLOR = '#078c4f'; // green
export const RUN_FAILED_COLOR = '#e11d48'; // red
// Edges into a node the run skipped or never reached. The node itself fades
// instead, but an edge is too thin to read at low opacity.
export const RUN_SKIPPED_EDGE_COLOR = '#c7c7c7'; // grey

// Background of hover cards, standing in for antd's dark tooltip default.
export const TOOLTIP_BACKGROUND_COLOR = 'var(--surface-elevated)';

export const TRIGGER_ICON_COLOR = '#266ef1'; // blue
export const ACTION_ICON_COLOR = '#D1899F'; // dark yellow
export const CONTROL_FLOW_ICON_COLOR = '#E09D64'; // green
export const NOTIFICATIONS_ICON_COLOR = '#62BBE1';

// The "marker" visual at the end of an edge (an arrow)
export const LBEDGE_MARKER_END = {
  type: 'arrowclosed' as const,
  color: EDGE_COLOR,
};

// ReactFlow canvas default zoom level
export const DEFAULT_ZOOM_LEVEL = 1.2;

// ReactFlow fitView padding (adds breathing room around nodes)
export const FIT_VIEW_PADDING = 0.2;

// Node origin: [0.5, 0] means position represents top-center of the node
// This enables centered child positioning when x-coordinates match
export const NODE_ORIGIN: [number, number] = [0.5, 0];

// Fixed node dimensions (all nodes have same size)
export const NODE_WIDTH = 225;
export const NODE_HEIGHT = 48;

// Vertical spacing between parent and child nodes
export const CHILD_NODE_Y_OFFSET = 120;

// Horizontal spacing between sibling nodes (for MULTIPLE output type parents)
export const SIBLING_NODE_X_OFFSET = 250;

// Horizontal gap between adjacent sibling subtrees, measured edge-to-edge.
// Derived so that two leaf siblings (each NODE_WIDTH wide) land at centers
// exactly SIBLING_NODE_X_OFFSET apart.
export const SIBLING_SUBTREE_GAP = SIBLING_NODE_X_OFFSET - NODE_WIDTH;

// Subgraph parent node styling constants
export const SUBGRAPH_PADDING_X = 40; // Horizontal padding inside container
export const SUBGRAPH_PADDING_Y = 70; // Vertical padding at bottom (must fit plus button extension)

// SUBGRAPH_PARENT renders its header sticking up this many pixels above the
// dashed-box top. The layout pushes the SP container down by the same amount
// so the header lands on the outer-row Y (where regular siblings sit).
export const SUBGRAPH_HEADER_OVERFLOW = 20;

// Inset for the SP-header-to-inner-root edge tip: ends this many pixels above
// the inner root so the arrowhead lands just outside the target rather than
// overlapping it.
export const SUBGRAPH_EDGE_ARROW_INSET = 3;

// Radius used at orthogonal-edge corners (source-to-target rounded bends).
export const EDGE_CORNER_RADIUS = 12;

// NodePicker positioning constants
export const NODE_PICKER_WIDTH = 200; // Matches CSS min-width
export const NODE_PICKER_HEIGHT = 400; // Matches CSS max-height
export const PLUS_BUTTON_RADIUS = 12; // Half of 24px button
export const NODE_PICKER_GAP = 8; // Spacing between button and picker

// Where a banner about the selected run floats: centred over the canvas, clear
// of the tabs above it and in front of the graph behind it.
export const RUN_BANNER_TOP = 72;
export const RUN_BANNER_Z_INDEX = 5;
export const RUN_BANNER_MAX_WIDTH = 560;

// Where the on-canvas Copilot entry button floats: left-anchored per the
// reference screenshot, clear of the tabs bar above it (same clearance as
// RUN_BANNER_TOP). Below the chat panel's z-index (9) so the button sits
// invisibly behind it in the brief instant before it unmounts on open.
export const COPILOT_BUTTON_TOP = 72;
export const COPILOT_BUTTON_LEFT = 20;
export const COPILOT_BUTTON_Z_INDEX = 5;

// Canvas container sizing to fill the dashboard layout without scrollbar.
// Must match Layout.scss: .header height (64px) + .main vertical padding (28px).
const DASHBOARD_HEADER_HEIGHT = 64;
const DASHBOARD_MAIN_PADDING_Y = 28;
export const CANVAS_HEIGHT = `calc(100vh - ${DASHBOARD_HEADER_HEIGHT + DASHBOARD_MAIN_PADDING_Y}px)`;
export const CANVAS_MARGIN_BOTTOM = -DASHBOARD_MAIN_PADDING_Y;

// Size for the canvas loading spinners.
export const LOADING_SPINNER_SIZE = 'large' as const;

// Centres a loading spinner above its caption. Callers add how it fills its
// space: the whole page, or an overlay on the canvas.
export const LOADING_STACK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 20,
};

// The caption under a canvas loading spinner.
export const LOADING_LABEL_STYLE: CSSProperties = {
  fontSize: 16,
  color: 'var(--text-secondary)',
};
